import { type RefObject, useEffect } from 'react';

/**
 * While `open`, everything on the page except the dialog is out of reach:
 * each child of <body> that does not hold the dialog gets `inert` (no tap, no
 * Tab, no find-in-page) and `aria-hidden="true"` (gone from the screen
 * reader's virtual cursor, for the engines that do not read `inert` yet).
 * The dialog must therefore be portalled to <body> (ConfirmModal is).
 *
 * Only what this hook set is undone on close: an element that was already
 * inert or aria-hidden (a closed drawer, a decorative layer) keeps it. A layer
 * that mounts on <body> AFTER the dialog opened (a toast container created
 * late, a second portal) is not touched; it sits under the dialog's scrim.
 *
 * A <body> child carrying INERT_EXEMPT_ATTR is never touched either: the
 * toast container's live region, so a toast raised while a dialog is up is
 * still announced (b-011).
 */
export const INERT_EXEMPT_ATTR = 'data-inert-exempt';

export function useInertBackground(open: boolean, dialogRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const doc = dialog.ownerDocument;
    const touched: Array<{ el: HTMLElement; inert: boolean; hidden: boolean }> = [];
    for (const el of Array.from(doc.body.children)) {
      if (!(el instanceof HTMLElement) || el.contains(dialog)) continue;
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'TEMPLATE') continue;
      if (el.hasAttribute(INERT_EXEMPT_ATTR)) continue;
      const inert = el.hasAttribute('inert');
      const hidden = el.getAttribute('aria-hidden') === 'true';
      if (inert && hidden) continue;
      if (!inert) el.setAttribute('inert', '');
      if (!hidden) el.setAttribute('aria-hidden', 'true');
      touched.push({ el, inert, hidden });
    }
    return () => {
      for (const { el, inert, hidden } of touched) {
        if (!inert) el.removeAttribute('inert');
        if (!hidden) el.removeAttribute('aria-hidden');
      }
    };
  }, [open, dialogRef]);
}
